import React, { Component } from "react";
import { connect } from 'react-redux';
import { push } from "react-router-redux";
import classNames from "classnames";
import compressImage from "../../../utils/compress-image";
import {
  includes,
  startCase,
  isEmpty,
  isString,
  every,
} from "lodash";

import {
  fetchInvoice,
  removeInvoices,
  submitInvoices,
  updateInvoiceWithProgress,
} from "../invoicesActions";

import {
  fetchPurchaseOrder,
  removePurchaseOrders,
  submitPurchaseOrders,
  updatePurchaseOrderWithProgress,
} from "../purchaseOrdersActions"; 
import Layout from "../../shared/components/layouts/MainLayout";
import FileUpload from  "../../shared/components/forms/FileUpload";
import PreviewList from  "../../shared/components/forms/PreviewList";

import {
  InvoiceUploadExplanation,
  PurchaseOrderUploadExplanation,
} from "./TradeDocumentExplanation";

import { segmentEventTracker } from "../../../utils/SegmentEventTracker";
import { PageViewName, TrackEventName } from "../../shared/constants/SegmentEvents";
import { fetchCreditLine } from "../../CreditLimit/actions";
import { isExpiringWithinDays } from '../../shared/utils/DateCalculations';

export class UploadForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      errors: [],
      loading: false,
      attachments: [],
    };

    this.attachments = React.createRef();
  }

  componentWillMount() {
    this.removeExistingTradeDocs();
    this.props.fetchCreditLine();
    if (this.props.match.params.id) this.fetchTradeDocument();
  }

  componentDidMount(){
    const pageType = this.props.type === 'invoice' ?
      PageViewName.INVOICE_UPLOAD_VIEWED : PageViewName.PURCHASE_ORDER_UPLOAD_VIEWED;

    segmentEventTracker.trackPageView(pageType)
  }

  get titleizedType() {
    const { rclEndTimestamp } = this.props;
    if (!rclEndTimestamp || isExpiringWithinDays(rclEndTimestamp, 30))
      return 'Revolving Credit Line';
    return `${startCase(this.props.type)} Financing`;
  }

  get pluralizedType() {
    return `${this.props.type}s`;
  }

  get allFilesForDestroy() {
    return every(this.state.attachments, (a) => { return a._destroy });
  }

  get isContinueDisabled() {
    return this.state.loading ||
      isEmpty(this.state.attachments) ||
      this.allFilesForDestroy
  }

  get documentID() {
    return this.props.type === "invoice" ?
      this.props.invoice.invoice_document.id :
      this.props.purchase_order.purchase_order_document.id;
  }

  fetchTradeDocument = () => {
    if (this.props.type === "invoice") {
      return this.props.fetchInvoice(
        this.props.match.params.id,
        this.props.user.id,
        this.onDraftFetchSuccess,
        () => {}
      )
    }

    return this.props.fetchPurchaseOrder(
      this.props.match.params.id,
      this.props.user.id,
      this.onDraftFetchSuccess,
      () => {}
    )
  }

  removeExistingTradeDocs = () => {
    if (this.props.type === "invoice") return this.props.removeInvoices();
    return this.props.removePurchaseOrders();
  }

  onBack = () => {
    segmentEventTracker.trackEvent(TrackEventName.BACK_CLICKED);
    if (this.props.user.onboarding_finished) return this.props.push("/applications");
    return this.props.push("/");
  }

  validate(files) {
    const validTypes = ['application/pdf', 'image/jpg', 'image/jpeg', 'image/png'];
    let errors = [];
    let validatedFiles = files
      .filter((file) => {
        if(file.size > 26214400) errors.push({file: file, error: 'File exceeds 25mb'});
        return file.size <= 26214400;
      })
      .filter((file) => {
        if(!includes(validTypes, file.type)) errors.push({file: file, error: 'File is not an image or pdf'});
        return includes(validTypes, file.type);
      })

    return {
      files: validatedFiles,
      errors: errors,
    }
  }

  onDraftFetchSuccess = (data) => {
    const attachments = this.props.type === "invoice" ? data.invoice_document.invoice_attachments : data.purchase_order_document.purchase_order_attachments;
    this.setState({ attachments });
  }

  onChange = (e) => {
    const validatedFiles = this.validate(Array.from(e.target.files));
    const attachments = this.state.attachments.concat(validatedFiles.files);
    this.setState({ attachments });
    this.setState({ errors: validatedFiles.errors });
  }

  onRemove = (e) => {
    segmentEventTracker.trackEvent(TrackEventName.REMOVE_FILE_CLICKED);
    this.attachments.current.clear();
    this.setState({ attachments: e });
  }

  onSave = () => {
    segmentEventTracker.trackEvent(TrackEventName.SUBMIT_FORM_CLICKED);
    const oldFiles = Array.from(this.state.attachments.filter(a => a.id));
    const newFiles = Array.from(this.state.attachments.filter(a => !a.id));

    if (isEmpty(oldFiles) && isEmpty(newFiles)) return;
    this.setState({ loading: true }, () => {
      const compressedFilePromises = newFiles.map((file) => {
        return compressImage(file)
          .catch((error) => { return error.message })
          .then((file) => { return file })
      });

      Promise.all(compressedFilePromises)
      .then((results) => {
        const errors = results.filter(r => isString(r));
        const files = results.filter(r => r instanceof File || r instanceof Blob);
        if (isEmpty(errors)) {
          const attachments = oldFiles.concat(files);
            this.setState({ attachments }, () => {
            this.props.match.params.id ?
              this.updateDraftData() :
              this.sendDraftData();
          });
        } else {
          this.setState({ errors, loading: false });
        }
      });
    })
  }

  buildDraftData = () => {
    let formData = new FormData();

    this.state.attachments.forEach((attachment, index) => {
      formData.append(
        `${this.pluralizedType}[]${this.props.type}_attachments[]`,
        attachment,
        attachment.name,
      );
    })

    formData.append(`${this.pluralizedType}[]${this.props.type}_document[status]`, "draft");
    formData.append(
      `${this.pluralizedType}[]${this.props.type}[loan_application_id]`,
      this.props.user.loan_application_id
    );

    return formData;
  }

  buildUpdateDraftData = () => {
    let formData = new FormData();

    this.state.attachments.forEach((attachment, index) => {
      if (attachment.id && !attachment._destroy) return;
      const attachment_file = attachment.id ? attachment.url : attachment;

      formData.append(
        `${this.props.type}[${this.props.type}_document_attributes[id]]`,
        this.documentID
      );

      formData.append(
        `${this.props.type}[${this.props.type}_document_attributes[${this.props.type}_attachments_attributes[][id]]]`,
        attachment.id || ""
      );

      formData.append(
        `${this.props.type}[${this.props.type}_document_attributes[${this.props.type}_attachments_attributes[][_destroy]]]`,
        !!attachment._destroy
      );

      if (!isString(attachment_file)) {
        formData.append(
          `${this.props.type}[${this.props.type}_document_attributes[${this.props.type}_attachments_attributes[][attachment_file]]]`,
          attachment_file,
          attachment.name,
        );
      }
    });

    return formData;
  }

  sendDraftData = () => {
    if (this.props.type === "invoice") {
      this.props.submitInvoices(
        this.buildDraftData(),
        () => {},
        (data) => {
          this.setState({ loading: false });
          this.props.push(`/invoices/${data.id}/add_contact`);
        }
      );
    } else {
      this.props.submitPurchaseOrders(
        this.buildDraftData(),
        () => {},
        (data) => {
          this.setState({ loading: false });
          this.props.push(`/purchase_orders/${data.id}/add_contact`);
        }
      );
    }
  }

  updateDraftData = () => {
    if (this.props.type === "invoice") {
        if (!this.buildUpdateDraftData().has("invoice[invoice_document_attributes[id]]")) {
          return this.props.push(`/${this.props.type}s/${this.props.match.params.id}/add_contact`);
        }
        this.props.updateInvoice(
        this.props.match.params.id,
        this.props.user.id,
        this.buildUpdateDraftData(),
        (data) => {
          this.setState({ loading: false });
          this.props.push(`/invoices/${data.id}/add_contact`);
        },
        () => {},
      );
    } else {
      if (!this.buildUpdateDraftData().has("purchase_order[purchase_order_document_attributes[id]]")) {
        return this.props.push(`/${this.props.type}s/${this.props.match.params.id}/add_contact`);
      }
      this.props.updatePurchaseOrder(
        this.props.match.params.id,
        this.props.user.id,
        this.buildUpdateDraftData(),
        (data) => {
          this.setState({ loading: false });
          this.props.push(`/purchase_orders/${data.id}/add_contact`);
        },
        () => {},
      );
    }
  }

  get buttonClassnames() {
    return classNames(
      "ds-btn",
      "--full",
      "--medium",
      { "_loading": this.state.loading },
      { "_disabled": this.isContinueDisabled },
    );
  }

  renderSaveButton = () => {
    return (
      <button
        onClick={this.onSave}
        className={this.buttonClassnames}
        disabled={this.isContinueDisabled}
        data-test='upload-invoice-submit'
      >
        Continue to Step 2
      </button>
    );
  }

  renderAttachments = () => {
    if (this.allFilesForDestroy) return;
    return (
      <PreviewList
        disableTip={true}
        loading={this.state.loading}
        attachments={this.state.attachments}
        onChange={(_, e) => { this.onRemove(e) }}
        disableAction={this.state.loading}
        errors={this.state.errors}
      />
    );
  }

  renderTradeDocCopy = () => {
    return this.props.type === "invoice" ?
      <InvoiceUploadExplanation /> :
      <PurchaseOrderUploadExplanation />;
  }

  trackUploadClick = () => {
    segmentEventTracker.trackEvent(TrackEventName.UPLOAD_FILE_CLICKED);
  }

  render() {
    return(
      <Layout
        header={`${this.titleizedType} Application`}
        backButtonAction={this.onBack}
        gridClassName="--narrow"
        showQuickNav={false}
      >
        <div className="ds-card _tight">
          {this.renderTradeDocCopy()}
          <FileUpload
            multiple={true}
            ref={this.attachments}
            onChange={this.onChange}
            buttonText="Upload All Pages (Photos or PDF)"
            onClick={this.trackUploadClick}
          />
          {this.renderAttachments()}
          {this.renderSaveButton()}
        </div>
      </Layout>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    user: state.user,
    invoice: state.invoices.present,
    purchase_order: state.purchase_orders.present,
    rclEndTimestamp: state.creditLine.end_timestamp,
  };
}

const mapDispatchToProps = (dispatch, props) => {
  return {
    push: (url) => dispatch(push(url)),
    removeInvoices: () => dispatch(removeInvoices()),
    removePurchaseOrders: () => dispatch(removePurchaseOrders()),
    fetchInvoice: (id, userID, onSuccess, onError) => dispatch(fetchInvoice(id, userID, onSuccess, onError)),
    fetchPurchaseOrder: (id, userID, onSuccess, onError) => dispatch(fetchPurchaseOrder(id, userID, onSuccess, onError)),
    submitInvoices: (formData, onProgress, successCallback) => dispatch(submitInvoices(formData, onProgress, successCallback)),
    submitPurchaseOrders: (formData, onProgress, successCallback) => dispatch(submitPurchaseOrders(formData, onProgress, successCallback)),
    updateInvoice: (id, userID, body, successCallback, onProgress) => dispatch(updateInvoiceWithProgress(id, userID, body, successCallback, onProgress)),
    updatePurchaseOrder: (id, userID, body, successCallback, onProgress) => dispatch(updatePurchaseOrderWithProgress(id, userID, body, successCallback, onProgress)),
    fetchCreditLine: () => dispatch(fetchCreditLine())
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UploadForm);



// WEBPACK FOOTER //
// ./src/features/trade_documents/components/UploadForm.js